/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */

import {
  createAssetSearchTerms,
  validateAssetGroups
} from '@ion/core/src/entities/Asset';
import {
  validateAttributes
} from '@ion/core/src/entities/EntityUtils';
import {
  createRoleSearchTerms,
  validateRoleGroups
} from '@ion/core/src/entities/Roles';
import {getApplicationContext} from '@ion/core/src/environment/environment';
import {error} from '@ion/core/src/logger/logger';
import {
  bulkSearchAssets,
  bulkSearchDrivers,
  bulkSearchIOProfiles,
  bulkSearchRoles,
  bulkSearchSchedules,
  bulkUpdateAccounts,
  bulkUpdateAssets,
  bulkUpdateDevices,
  bulkUpdateOperators,
  createAssetDevices,
  deleteAssetDevices,
  getDevice,
  updateOperators
} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  getOperatorCategories,
  getOperatorTypes
} from '@ion/core/src/services/GroupProperties';
import {
  bulkUpdateUsers,
  getUserContext
} from '@ion/core/src/services/MicroServices';
import {
  Account,
  Asset,
  CTCEntityResponseWrapper,
  Entity,
  GenericObject,
  Operator,
  User
} from '@ion/core/src/types/entities';
import {ExtendedResults} from '@ion/core/src/types/services';
import {getAssetImportErrors} from '@ion/core/src/xlsx/AssetTemplate';
import {getDriverImportErrors} from '@ion/core/src/xlsx/DriverTemplate';
import {getGroupsImportErrors} from '@ion/core/src/xlsx/GroupsTemplate';
import {getUserImportErrors} from '@ion/core/src/xlsx/UserTemplate';
import {getGroups} from '@ion/dls/src/editors/GroupChooser';
import QueryKey from '@ion/query/src/config/QueryKeys';
import {removeQueries} from '@ion/query/src/utils/helpers';
import {hasDeviceAssociationEditAccess} from '@/security/Features';
import {refreshInitialization} from '@/store/actions/initialization';
import {getAllAccounts} from '../helpers';
import {
  createDeviceSearchTerms,
  createDriverSearchTerms,
  createIOProfileSearchTerms,
  createScheduleSearchTerms,
  getAssetsWithDeviceMoves,
  getCurrentAssetDevices,
  getDevicesToMove,
  getOperatorsToMove,
  resolveAssetDevices,
  resolveLinks,
  searchDevicesByIdentifiers,
  validateAccounts,
  validateDeviceAssociation,
  validateDeviceGroups,
  validateDrivers,
  validateIOProfileGroups,
  validateKeyfobBadgeIDValues,
  validateSchedules
} from './helpers';

// invoked when an import is initiated ...
export const IMPORT_REQUEST   = 'IMPORT_REQUEST';

// invoked when an import has completed ...
export const IMPORT_RESPONSE  = 'IMPORT_RESPONSE';

// invoked when an import has failed ...
export const IMPORT_ERROR     = 'IMPORT_ERROR';

// invoked when an import is confirmed after completion ...
export const IMPORT_CONFIRM   = 'IMPORT_CONFIRM';

export const importConfirm = (entity) => ({
  type : IMPORT_CONFIRM, entity
});

// locates the error a failed Device move left behind for the given Asset,
// matched by the Device link the Asset still carries ...
const deviceMoveErrorFor = (asset: Asset, deviceMoveErrors: GenericObject[]) => (
  deviceMoveErrors.find(deviceMoveError => (
    asset.devices?.some(deviceLink => entityId(deviceLink) === deviceMoveError.id)
  ))
);

// a failed Device move surfaces as a rejected bulk operation entry, which
// carries the backend's own rejection reason the same way Asset-level bulk
// errors do (see formatErrors in @ion/core/src/xlsx/TemplateHelpers) ...
const deviceMoveErrorMessage = (deviceMoveError: GenericObject): string => (
  deviceMoveError?.response?.data?.response?.errors?.[0] || deviceMoveError?.message
);

/**
 * Provides the Asset Export Action Creator which manages retrieving all assets
 * and converting the retrieved assets to a CSV for subsequent download.
 */
export const importAssets = (
  assets: CTCEntityResponseWrapper<Asset>[],
  isAutomaticReAssignmentDriver?: boolean,
  options?: GenericObject
) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = Entity.asset;
    dispatch({type: IMPORT_REQUEST, entity});
    try {
      const {
        globalGroup: {
          globalGroup
        }
      } = getState();
      const accountLinks         = await getAllAccounts(getState);
      let {resolved, unresolved} = validateAccounts<Asset>(accountLinks, assets, entity);
      const deviceSearch         = createDeviceSearchTerms(resolved);
      const driverSearch         = createDriverSearchTerms(resolved);
      const ioProfileSearch      = createIOProfileSearchTerms(resolved);
      const scheduleSearch       = createScheduleSearchTerms(resolved, entity);
      let drivers                = options?.driverSheet?.rows;
      let devices;
      let ioprofiles;
      let schedules;
      if (deviceSearch?.length) {
        devices = await searchDevicesByIdentifiers(deviceSearch, entityId(globalGroup));
      }
      if (!drivers?.length && driverSearch) {
        drivers = await bulkSearchDrivers(driverSearch);
      }
      if (ioProfileSearch) {
        ioprofiles = await bulkSearchIOProfiles(ioProfileSearch);
      }
      if (scheduleSearch) {
        schedules = await bulkSearchSchedules(scheduleSearch);
      }
      let invalidAssets = unresolved;

      if (devices) {
        // each identifier column is first resolved to the Device it matched,
        // so that the Asset is paired by the Device's id rather than by an
        // identifier which only an LMU is guaranteed to carry ...
        const {
          valid: resolvedDevices,
          invalid: unresolvedDevices
        } = resolveAssetDevices(resolved, devices);
        invalidAssets = invalidAssets.concat(unresolvedDevices);
        resolved = resolvedDevices;

        const {valid, invalid} = validateDeviceGroups(resolved, devices);
        invalidAssets = invalidAssets.concat(invalid);
        resolved = valid;

        // a user who may not link or de-link Devices may not change the Devices
        // of an Asset, whether existing or new; a new Asset may therefore only be
        // created without a Device. Rows which list no Device leave the Devices
        // as they are, so this only applies when Devices were imported ...
        if (!hasDeviceAssociationEditAccess()) {
          const deviceAssociation = validateDeviceAssociation(
            resolved,
            await getCurrentAssetDevices(resolved.filter(({asset}) => asset?.devices?.length))
          );
          invalidAssets = invalidAssets.concat(deviceAssociation.invalid);
          resolved = deviceAssociation.valid;
        }
      }
      if (drivers) {
        const {valid, invalid} = validateDrivers(resolved, drivers, isAutomaticReAssignmentDriver);
        invalidAssets = invalidAssets.concat(invalid);
        resolved = valid;
      }

      if (ioprofiles) {
        const {valid, invalid} = validateIOProfileGroups(resolved, ioprofiles);
        invalidAssets = invalidAssets.concat(invalid);
        resolved = valid;
      }
      if (schedules) {
        const {valid, invalid} = validateSchedules(resolved, schedules, entity);
        invalidAssets = invalidAssets.concat(invalid);
        resolved = valid;
      }

      if (devices) {
        const devicesToMove = getDevicesToMove(resolved, devices);
        if (devicesToMove?.length) {
          // the CTC Platform rejects an Asset's own account change while a
          // Device is still associated to it, so the Device must be unpaired
          // first - the Asset's own bulk update below still carries this
          // Device's link unchanged, which re-pairs it in the new account
          // automatically, the same way the single Edit Asset flow does ...
          const assetsWithDeviceMoves = getAssetsWithDeviceMoves(resolved, devicesToMove);
          let devicesReadyToMove = devicesToMove;
          if (assetsWithDeviceMoves.length) {
            await deleteAssetDevices(assetsWithDeviceMoves);
            // disassociating a Device from its Asset bumps the Device's own
            // version server-side, so the versions resolved earlier when
            // these Devices were first searched/matched are now stale -
            // refresh them before the move, carrying over the target
            // account getDevicesToMove already resolved onto each one ...
            devicesReadyToMove = await Promise.all(devicesToMove.map(async(device) => {
              const [refreshed] = await getDevice(device.id);
              return {...refreshed, account: device.account};
            }));
          }
          // bulkUpdateDevices never rejects for an individual Device failure
          // (e.g. the ICN role error from ION-7555) - it resolves with the
          // failure captured in `errors`, which was previously discarded,
          // leaving a failed Device move with no record of why ...
          const {errors: deviceMoveErrors} = await bulkUpdateDevices(devicesReadyToMove);
          if (deviceMoveErrors?.length) {
            deviceMoveErrors.forEach(deviceMoveError => {
              error(`Failed to move Device ${deviceMoveError.name} to its Asset's new Group`, deviceMoveError);
            });
            const failedAssets = getAssetsWithDeviceMoves(resolved, deviceMoveErrors);
            await Promise.all(failedAssets.map(async(asset) => {
              try {
                // the Device was unpaired above in anticipation of its move -
                // since the move failed, re-pair it so the Asset is left
                // exactly as it was before the import, rather than orphaned ...
                await createAssetDevices(asset.id, asset.devices);
              } catch (rePairErr) {
                error(`Failed to re-pair Device back to Asset ${asset.name} after its move failed`, rePairErr);
              }
            }));
            // the Asset must not move either when its Device failed to move,
            // and the failure must be reported rather than counted as a
            // successful import ...
            const failedAssetIds = failedAssets.map(asset => asset.id);
            resolved = resolved.filter(({asset}) => !failedAssetIds.includes(asset.id));
            invalidAssets = invalidAssets.concat(failedAssets.map(asset => ({
              asset : {
                ...asset,
                isDeviceMoveFailed : true,
                deviceMoveError    : deviceMoveErrorMessage(deviceMoveErrorFor(asset, deviceMoveErrors))
              }
            })));
          }
        }
      }
      if (drivers) {
        const operatorsToMove = getOperatorsToMove(resolved, drivers);
        if (operatorsToMove?.length) {
          await updateOperators(operatorsToMove);
        }
      }

      const resolvedAssets = [];

      resolved.forEach(({asset}) => {
        resolvedAssets.push(asset);
      });
      const response = await bulkUpdateAssets(resolvedAssets);
      const result: ExtendedResults = Object.assign({}, response);
      const processedResult = getAssetImportErrors(invalidAssets, result, globalGroup);
      dispatch({type: IMPORT_RESPONSE, entity, result: processedResult, valid: !(result?.errors?.length > 0)});
      removeQueries(QueryKey.GET_ALL_DRIVERS, false);
      removeQueries(QueryKey.DEVICE_GET, false);
    } catch (err) {
      dispatch({type: IMPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the User Export Action Creator which manages retrieving all users
 * and converting the retrieved users to a CSV for subsequent download.
 */
export const importUsers = (users: CTCEntityResponseWrapper<User>[]) => {
  const context = getUserContext(getApplicationContext());
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = Entity.user;
    dispatch({type: IMPORT_REQUEST, entity});
    try {
      const {
        globalGroup: {
          globalGroup
        }
      } = getState();
      const accountLinks         = await getGroups(globalGroup);
      let {resolved, unresolved} = validateAccounts<User>(accountLinks, users, entity);
      const roleSearch           = createRoleSearchTerms(resolved);
      let roles;
      if (roleSearch) {
        roles = await bulkSearchRoles(roleSearch);
      }
      let invalidUsers = unresolved;

      if (roles) {
        const {valid, invalid} = validateRoleGroups(resolved, roles);
        invalidUsers = invalidUsers.concat(invalid);
        resolved = valid;
      }

      const resolvedUsers = [];

      resolved.forEach(({user}) => {
        user.username = user.username.trim();
        resolvedUsers.push(user);
      });

      const response = await bulkUpdateUsers(resolvedUsers, context);
      const result: ExtendedResults = Object.assign({}, response);
      const processedResult = getUserImportErrors(invalidUsers, result, globalGroup);
      dispatch({type: IMPORT_RESPONSE, entity, result: processedResult, valid: !(result?.errors?.length > 0)});
    } catch (err) {
      dispatch({type: IMPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Driver Export Action Creator which manages retrieving all Drivers
 * and converting the retrieved drivers to a CSV for subsequent download.
 */
export const importDrivers = (operators: CTCEntityResponseWrapper<Operator>[]) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = Entity.operator;
    dispatch({type: IMPORT_REQUEST, entity});
    try {
      const {
        globalGroup: {
          globalGroup
        }
      } = getState();
      const accountLinks         = await getAllAccounts(getState);
      const {
        initialization: {
          application
        }
      }                         = getState();
      let {resolved, unresolved} = validateAccounts<Operator>(accountLinks, operators, entity);
      const assetSearch          = createAssetSearchTerms(resolved);
      const scheduleSearch       = createScheduleSearchTerms(resolved, entity);
      const operatorTypes        = getOperatorTypes();
      const operatorCategories   = getOperatorCategories();
      let assets;
      let schedules;
      if (assetSearch) {
        assets = await bulkSearchAssets(assetSearch);
      }
      if (scheduleSearch) {
        schedules = await bulkSearchSchedules(scheduleSearch);
      }

      let invalidDrivers = unresolved;

      if (assets) {
        const {valid, invalid} = validateAssetGroups(resolved, assets);
        invalidDrivers = invalidDrivers.concat(invalid);
        resolved = valid;
      }

      if (schedules) {
        const {valid, invalid} = validateSchedules(resolved, schedules, entity);
        invalidDrivers = invalidDrivers.concat(invalid);
        resolved = valid;
      }

      {
        const {valid, invalid} = validateKeyfobBadgeIDValues(resolved);
        invalidDrivers = invalidDrivers.concat(invalid);
        resolved = valid;
      }

      resolved = validateAttributes({
        entities   : resolved,
        entityName : 'operator',
        propName   : 'type',
        props      : operatorTypes
      });
      resolved = validateAttributes({
        entities   : resolved,
        entityName : 'operator',
        propName   : 'operatorCategory',
        props      : operatorCategories
      });

      const resolvedDrivers = [];

      resolved?.forEach(({operator}) => {
        resolvedDrivers.push(operator);
      });

      const response = await bulkUpdateOperators(resolvedDrivers);
      const result: ExtendedResults = Object.assign({}, response);
      const processedResult = getDriverImportErrors(invalidDrivers, result, globalGroup);
      dispatch({type: IMPORT_RESPONSE, entity, result: processedResult, valid: !(result?.errors?.length > 0)});
      removeQueries(QueryKey.GET_ALL_DRIVERS, false);
    } catch (err) {
      dispatch({type: IMPORT_ERROR, entity});
      return error(err);
    }
  };
};

/**
 * Provides the Asset Export Action Creator which manages retrieving all assets
 * and converting the retrieved assets to a CSV for subsequent download.
 */
export const importGroups = (groups: CTCEntityResponseWrapper<Account>[]) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const entity = Entity.group;
    dispatch({type: IMPORT_REQUEST, entity});
    try {
      const {application} = getState().initialization.role;
      const accountLinks = await getAllAccounts(getState);
      const {resolved, unresolved: invalidGroups} = resolveLinks(groups, accountLinks, Entity.account);
      const resolvedAssets = [];
      resolved.forEach(({account}) => {
        resolvedAssets.push(account);
      });
      const response = await bulkUpdateAccounts(resolvedAssets, application);
      const result: ExtendedResults = Object.assign({}, response);
      const processedResult = getGroupsImportErrors(invalidGroups, result);
      await dispatch(refreshInitialization());
      dispatch({type: IMPORT_RESPONSE, entity, result: processedResult, valid: !(result?.errors?.length > 0)});
    } catch (err) {
      dispatch({type: IMPORT_ERROR, entity});
      return error(err);
    }
  };
};
